import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { WirelessSetupService } from '../../../common/services/wireless-setup.service';
import { NotificationService } from '../../../common/services/notification.service';
import { SpinnerService } from '../../../common/services/spinner.service';
import { LanguageStateService } from '../../../common/services/language-state.service';
import { CommonUtil } from '../../../common/util/common-util';

@Component({
    selector: 'app-otp-verification',
    templateUrl: './otp-verification.component.html',
    styleUrl: './otp-verification.component.scss',
    standalone: false
})
export class OtpVerificationComponent {
  // Supplied by the parent, sourced from Step 1/Step 2's events.
  @Input() wirelessSetupCustomerOrderPayload: any;
  @Input() accountNumber: number;
  @Input() selectedPinDeliveryMethod: string | null = null;
  @Input() pinDeliveryOptions: { email?: string; ctnOptions: string[] } = { ctnOptions: [] };
  @Input() showPinFields = false;
  @Input() isPinExpired = false;
  @Output() pinValidated = new EventEmitter<any>();
  @Output() connectionError = new EventEmitter<void>();

  fb: FormBuilder = inject(FormBuilder);
  wirelessSetupService = inject(WirelessSetupService);
  notificationService = inject(NotificationService);
  spinnerService = inject(SpinnerService);
  languageService: LanguageStateService = inject(LanguageStateService);

  form: FormGroup = this.fb.group({
    oneTimePin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
  });

  step3ErrorType: 'invalid' | null = null;
  locked = false;

  onlyNumbers(event: KeyboardEvent): boolean {
    return CommonUtil.onlyNumbers(event);
  }

  maskEmail(email?: string): string {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain || local.length <= 3) return email;
    const masked = local.slice(0, 2) + '-'.repeat(local.length - 3) + local.slice(-1);
    return `${masked}@${domain}`;
  }

  verifyPinClicked() {
    if (!this.form.get('oneTimePin')?.valid) {
      this.form.get('oneTimePin')?.markAsTouched();
      return;
    }

    this.spinnerService.setLoading(true, 'otp-verification-verifyPin');
    this.step3ErrorType = null;

    const isEmailSelected = this.selectedPinDeliveryMethod === 'email';
    const transactionNumberSource = isEmailSelected
      ? this.pinDeliveryOptions.ctnOptions?.[0]
      : this.selectedPinDeliveryMethod;
    const body = {
      TransactionNumber: this.sanitizePhoneNumber(transactionNumberSource || undefined),
      AccountNumber: this.accountNumber,
      PassCode: this.form.get('oneTimePin')?.value,
    };

    this.wirelessSetupService.getAccount(body, this.wirelessSetupCustomerOrderPayload)
      .pipe(finalize(() => this.spinnerService.setLoading(false, 'otp-verification-verifyPin')))
      .subscribe({
        next: (resp: any) => {
          const viResp = this.retrieveWirelessResponse(resp);
          if (viResp.Code === '0000') {
            this.pinValidated.emit(resp);
          } else if (!this.isPinExpired) {
            this.step3ErrorType = 'invalid';
          }
        },
        error: () => {
          this.connectionError.emit();
        }
      });
  }

  lockAfterValidation() {
    this.locked = true;
    this.form.get('oneTimePin')?.disable();
  }

  reset() {
    this.form.reset();
    this.form.get('oneTimePin')?.enable();
    this.step3ErrorType = null;
    this.locked = false;
  }

  private sanitizePhoneNumber(value?: string) {
    return (value || '').replace(/-/g, '');
  }

  private retrieveWirelessResponse(resp: any) {
    return resp.ResponseHeader?.ProductResponse?.find(
      (response: any) => response.Type.toLowerCase() === 'wireless'
    );
  }
}